import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  Filter,
  RefreshCcw,
  Search,
  SlidersHorizontal,
  Star,
} from "lucide-react";

import ReviewCard from "../../components/dashboard/production/ReviewCard";
import PaginationFooter from "../../components/dashboard/production/PaginationFooter";

const tabs = [
  { key: "all", label: "All Reviews" },
  { key: "needs_response", label: "Needs Response" },
  { key: "pending_approval", label: "Pending Approval" },
  { key: "responded", label: "Responded" },
  { key: "archived", label: "Archived" },
];

const demoBusiness = {
  name: "Joe’s Coffee Shop",
};

const demoReviews = [
  {
    id: 1,
    authorName: "Sarah Mitchell",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 8).toISOString(),
    content:
      "Absolutely love this place! The staff was friendly, the service was fast, and everything exceeded my expectations.",
    suggestedResponse:
      "Thank you so much, Sarah! We’re thrilled to hear you had such a great experience with our team. We appreciate your support and hope to see you again soon!",
    responseText: "",
    status: "draft_generated",
  },
  {
    id: 2,
    authorName: "Chris Parker",
    rating: 3,
    reviewDate: new Date(Date.now() - 1000 * 60 * 34).toISOString(),
    content:
      "Great experience overall, but communication could have been better. I had to wait longer than expected.",
    suggestedResponse:
      "Thank you for your feedback, Chris. We’re glad you had a good overall experience, and we’re sorry the communication and wait time didn’t meet expectations.",
    responseText: "",
    status: "needs_approval",
  },
  {
    id: 3,
    authorName: "Amanda Lewis",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 72).toISOString(),
    content:
      "Amazing service. Everyone was helpful and the coffee was perfect. This is definitely becoming my new regular spot.",
    suggestedResponse:
      "Thank you so much, Amanda! We’re so happy to hear you enjoyed the service and coffee.",
    responseText:
      "Thank you so much, Amanda! We’re so happy to hear you enjoyed the service and coffee.",
    status: "posted",
  },
  {
    id: 4,
    authorName: "Michael Johnson",
    rating: 4,
    reviewDate: new Date(Date.now() - 1000 * 60 * 115).toISOString(),
    content:
      "Really nice atmosphere and great drinks. It was a little crowded, but the team handled it well.",
    suggestedResponse:
      "Thank you, Michael! We’re glad you enjoyed the atmosphere and drinks. We appreciate your patience during a busy time.",
    responseText: "",
    status: "draft_generated",
  },
  {
    id: 5,
    authorName: "Nina Patel",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 190).toISOString(),
    content:
      "The team was so welcoming and the latte was excellent. Highly recommend stopping in.",
    suggestedResponse:
      "Thank you, Nina! We’re thrilled you enjoyed your latte and felt welcomed by our team.",
    responseText:
      "Thank you, Nina! We’re thrilled you enjoyed your latte and felt welcomed by our team.",
    status: "posted",
  },
  {
    id: 6,
    authorName: "David Brooks",
    rating: 2,
    reviewDate: new Date(Date.now() - 1000 * 60 * 260).toISOString(),
    content:
      "My order was incorrect and it took a while to fix. The staff was polite, but the experience was frustrating.",
    suggestedResponse:
      "Thank you for letting us know, David. We’re sorry your order was incorrect and that it took longer than expected to make right.",
    responseText: "",
    status: "needs_approval",
  },
  {
    id: 7,
    authorName: "Emily Carter",
    rating: 5,
    reviewDate: new Date(Date.now() - 1000 * 60 * 360).toISOString(),
    content:
      "Great coffee, clean space, and friendly people. Exactly what I was hoping for.",
    suggestedResponse:
      "Thank you, Emily! We’re so glad you enjoyed the coffee, space, and service.",
    responseText:
      "Thank you, Emily! We’re so glad you enjoyed the coffee, space, and service.",
    status: "posted",
  },
];

export default function DemoReviews() {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [sortMode, setSortMode] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);

  const [page, setPage] = useState(1);
  const pageSize = 10;

  const reviews = demoReviews;

  const stats = useMemo(() => {
    const total = 248;
    const needsResponse = reviews.filter(
      (r) =>
        !r.responseText &&
        [
          "pending_response",
          "draft_generated",
          "new",
          "needs_approval",
        ].includes(r.status),
    ).length;

    const responded = 228;
    const lowRating = 21;

    return {
      total,
      needsResponse,
      responded,
      lowRating,
    };
  }, [reviews]);

  const filteredReviews = useMemo(() => {
    const value = search.trim().toLowerCase();

    let filtered = [...reviews];

    if (value) {
      filtered = filtered.filter((review) => {
        return (
          review.authorName?.toLowerCase().includes(value) ||
          review.content?.toLowerCase().includes(value) ||
          review.status?.toLowerCase().includes(value) ||
          String(review.rating).includes(value)
        );
      });
    }

    switch (activeTab) {
      case "needs_response":
        filtered = filtered.filter(
          (r) =>
            !r.responseText &&
            ["pending_response", "draft_generated", "new"].includes(r.status),
        );
        break;

      case "pending_approval":
        filtered = filtered.filter((r) =>
          ["needs_approval", "draft_generated"].includes(r.status),
        );
        break;

      case "responded":
        filtered = filtered.filter(
          (r) => r.responseText || r.status === "posted",
        );
        break;

      case "archived":
        filtered = filtered.filter((r) => r.status === "archived");
        break;

      default:
        break;
    }

    if (ratingFilter !== "all") {
      filtered = filtered.filter(
        (r) => Number(r.rating) === Number(ratingFilter),
      );
    }

    if (sortMode === "newest") {
      filtered.sort((a, b) => new Date(b.reviewDate) - new Date(a.reviewDate));
    }

    if (sortMode === "oldest") {
      filtered.sort((a, b) => new Date(a.reviewDate) - new Date(b.reviewDate));
    }

    if (sortMode === "highest_rating") {
      filtered.sort((a, b) => Number(b.rating || 0) - Number(a.rating || 0));
    }

    if (sortMode === "lowest_rating") {
      filtered.sort((a, b) => Number(a.rating || 0) - Number(b.rating || 0));
    }

    return filtered;
  }, [reviews, search, activeTab, ratingFilter, sortMode]);

  useEffect(() => {
    setPage(1);
  }, [search, activeTab, ratingFilter, sortMode]);

  const paginatedReviews = useMemo(() => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;

    return filteredReviews.slice(start, end);
  }, [filteredReviews, page]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Reviews
          </h1>

          <p className="text-slate-500 mt-1">
            Browse, search, and monitor customer feedback for{" "}
            <span className="font-medium text-slate-700">
              {demoBusiness.name}
            </span>
            .
          </p>
        </div>

        <button className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium transition flex items-center gap-2">
          <RefreshCcw size={16} />
          Sync Reviews
        </button>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
        <ReviewStatCard
          title="Total Reviews"
          value={stats.total}
          subtitle="Synced from Google"
          icon={<Star size={22} className="text-indigo-600" />}
          iconBg="bg-indigo-100"
        />

        <ReviewStatCard
          title="Needs Response"
          value={stats.needsResponse}
          subtitle="Reviews awaiting action"
          icon={<AlertCircle size={22} className="text-orange-600" />}
          iconBg="bg-orange-100"
        />

        <ReviewStatCard
          title="Responded"
          value={stats.responded}
          subtitle="Reviews with owner replies"
          icon={<CheckCircle size={22} className="text-green-600" />}
          iconBg="bg-green-100"
        />

        <ReviewStatCard
          title="Low Rating"
          value={stats.lowRating}
          subtitle="3 stars or below"
          icon={<AlertCircle size={22} className="text-red-600" />}
          iconBg="bg-red-100"
        />
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4 space-y-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => {
            const active = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                  active
                    ? "bg-indigo-600 text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by reviewer, rating, status, or keyword..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-indigo-300 focus:bg-white focus:ring-4 focus:ring-indigo-50"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Filter size={17} className="text-slate-400" />

              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="highest_rating">Highest rating</option>
                <option value="lowest_rating">Lowest rating</option>
              </select>
            </div>

            <button
              onClick={() => setShowFilters((current) => !current)}
              className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100 transition flex items-center justify-center gap-2 text-sm font-medium"
            >
              <SlidersHorizontal size={16} />
              Filters
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                  Rating
                </label>

                <select
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-300 focus:ring-4 focus:ring-indigo-50"
                >
                  <option value="all">All ratings</option>
                  <option value="5">5 stars</option>
                  <option value="4">4 stars</option>
                  <option value="3">3 stars</option>
                  <option value="2">2 stars</option>
                  <option value="1">1 star</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
          <p className="text-sm text-slate-600">
            Showing{" "}
            <span className="font-semibold text-slate-900">
              {filteredReviews.length}
            </span>{" "}
            review{filteredReviews.length === 1 ? "" : "s"}
          </p>

          {(search || ratingFilter !== "all" || activeTab !== "all") && (
            <button
              onClick={() => {
                setSearch("");
                setRatingFilter("all");
                setActiveTab("all");
              }}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
            >
              Clear filters
            </button>
          )}
        </div>
      </section>

      {filteredReviews.length > 0 ? (
        <>
          <div className="space-y-4">
            {paginatedReviews.map((review) => (
              <ReviewCard
                key={review.id}
                name={review.authorName}
                date={formatDate(review.reviewDate)}
                rating={review.rating}
                review={review.content}
                responseText={review.responseText}
                suggestedResponse={review.suggestedResponse}
                status={mapStatus(review.status)}
              />
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <PaginationFooter
              page={page}
              pageSize={pageSize}
              totalItems={filteredReviews.length}
              onPageChange={setPage}
            />
          </div>
        </>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
          <h2 className="text-lg font-semibold text-slate-900">
            No reviews found
          </h2>

          <p className="text-slate-500 mt-2">
            Try adjusting your search or filters.
          </p>
        </div>
      )}
    </div>
  );
}

function ReviewStatCard({ title, value, subtitle, icon, iconBg }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <h3 className="text-3xl font-bold text-slate-900 mt-2">{value}</h3>
          <p className="text-sm text-slate-500 mt-2">{subtitle}</p>
        </div>

        <div
          className={`h-11 w-11 rounded-2xl flex items-center justify-center ${iconBg}`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function formatDate(date) {
  if (!date) return "Unknown date";

  return new Date(date).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function mapStatus(status) {
  if (!status) return "needs_response";

  if (
    status === "pending_response" ||
    status === "draft_generated" ||
    status === "needs_approval" ||
    status === "new"
  ) {
    return "needs_response";
  }

  return status;
}
